import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Package, Clock, CheckCircle, Truck, XCircle, ChevronRight, ChevronLeft,
  ArrowLeft, MapPin, AlertCircle, Search, ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrency } from '@/lib/currency-context';
import { useCart } from '@/lib/cart-context';
import { orders as ordersApi, products as productsApi } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { productPriceCurrency } from '@/lib/productTax';
import { toast } from 'sonner';
import { buildProductPath } from '@/lib/product-url';
import { getApiLangParam } from '@/lib/product-i18n';
import { formatSelectedVariant, productNeedsVariant } from '@/lib/variant';

const IN_PROGRESS = new Set(['pending', 'confirmed', 'processing', 'shipped']);
const RETURNS = new Set(['returned', 'refunded', 'return']);
const UNDELIVERABLE = new Set(['undeliverable', 'failed_delivery']);

const OrdersPage = () => {
  const { t, i18n } = useTranslation();
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { addItem } = useCart();
  const [orders, setOrders] = useState([]);
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailError, setDetailError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail(orderId);
    } else {
      setOrderDetail(null);
      setDetailError(null);
      fetchOrders();
    }
  }, [orderId]);

  useEffect(() => {
    if (orderId) return undefined;
    let cancelled = false;
    productsApi
      .list({ limit: 12, lang: getApiLangParam(i18n.language) })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.products || res.data || [];
        setRecommended(Array.isArray(list) ? list.slice(0, 8) : []);
      })
      .catch(() => {
        if (!cancelled) setRecommended([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, i18n.language]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersApi.list();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetail = async (id) => {
    try {
      setLoading(true);
      setDetailError(null);
      const response = await ordersApi.get(id);
      setOrderDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      setOrderDetail(null);
      setDetailError(error.response?.status === 404 ? 'notFound' : 'loadError');
    } finally {
      setLoading(false);
    }
  };

  const formatOrderDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat(i18n.language || 'tr', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-gray-500" />;
      case 'confirmed': return <Clock className="w-4 h-4 text-gray-500" />;
      case 'processing': return <Clock className="w-4 h-4 text-gray-500" />;
      case 'shipped': return <Truck className="w-4 h-4 text-indigo-500" />;
      case 'delivered': return <CheckCircle className="w-4 h-4 text-[#2E7D32]" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-purple-100 text-purple-800';
      case 'shipped': return 'bg-indigo-100 text-indigo-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    const key = `orders.status.${status}`;
    const label = t(key);
    return label !== key ? label : status;
  };

  const getCardStatusLabel = (status) => {
    const key = `orders.statusCard.${status}`;
    const label = t(key);
    return label !== key ? label : getStatusLabel(status);
  };

  const formatOrderNumber = (order) =>
    order.order_number || `#${order.id.slice(0, 8)}`;

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (orders || []).filter((order) => {
      const status = order.status || '';
      if (filter === 'inProgress' && !IN_PROGRESS.has(status)) return false;
      if (filter === 'cancelled' && status !== 'cancelled') return false;
      if (filter === 'returns' && !RETURNS.has(status)) return false;
      if (filter === 'undeliverable' && !UNDELIVERABLE.has(status)) return false;
      if (!q) return true;
      const number = String(formatOrderNumber(order)).toLowerCase();
      const names = (order.items || []).map((i) => (i.product_name || '').toLowerCase()).join(' ');
      return number.includes(q) || names.includes(q);
    });
  }, [orders, filter, searchQuery]);

  const renderShippingAddress = (address) => {
    if (!address) return null;
    return (
      <>
        {address.full_name && <>{address.full_name}<br /></>}
        {(address.address_line1 || address.address) && (
          <>{address.address_line1 || address.address}<br /></>
        )}
        {address.address_line2 && <>{address.address_line2}<br /></>}
        {address.city}
        {address.state ? `, ${address.state}` : ''}
        {address.postal_code ? ` ${address.postal_code}` : ''}
        <br />
        {address.country}
        {address.phone && (
          <>
            <br />
            {address.phone}
          </>
        )}
      </>
    );
  };

  const renderLoading = () => (
    <div className="bg-[#F5F5F5] min-h-[50vh] py-6">
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <Skeleton className="h-10 w-48 mb-6" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return renderLoading();
  }

  if (orderId) {
    if (detailError) {
      return (
        <div className="py-8" data-testid="order-detail-error">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/orders"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('orders.backToOrders')}
            </Link>
            <div className="bg-white border rounded-xl p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">{t(`orders.${detailError}`)}</p>
              <Link to="/orders" className="mt-6 inline-block">
                <Button variant="outline" className="rounded-full">
                  {t('orders.backToOrders')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      );
    }

    if (!orderDetail) {
      return renderLoading();
    }

    const order = orderDetail;

    return (
      <div className="bg-[#F5F5F5] min-h-screen py-6" data-testid="order-detail-page">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <Link
            to="/orders"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
            data-testid="back-to-orders"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('orders.backToOrders')}
          </Link>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white rounded-xl border p-4 md:p-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{t('orders.orderDetails')}</h1>
              <p className="text-muted-foreground mt-1">
                {t('orders.orderNumber')}: {formatOrderNumber(order)}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('orders.placedOn')} {formatDate(order.created_at)}
              </p>
            </div>
            <Badge className={`${getStatusColor(order.status)} border-0 w-fit`}>
              {getStatusIcon(order.status)}
              <span className="ml-1">{getStatusLabel(order.status)}</span>
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {order.shipping_address && (
              <div className="bg-white border rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-[#FF6000]" />
                  <h2 className="font-semibold">{t('track.shippingAddress')}</h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  {renderShippingAddress(order.shipping_address)}
                </p>
              </div>
            )}

            <div className="bg-white border rounded-xl p-6">
              <h2 className="font-semibold mb-4">{t('track.orderSummary')}</h2>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('cart.subtotal')}</dt>
                  <dd>{formatPrice(order.subtotal ?? 0)}</dd>
                </div>
                {(order.tax ?? 0) > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">{t('cart.tax')}</dt>
                    <dd>{formatPrice(order.tax)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('cart.shipping')}</dt>
                  <dd>
                    {(order.shipping_cost ?? 0) > 0
                      ? formatPrice(order.shipping_cost)
                      : t('cart.free')}
                  </dd>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <dt>{t('cart.total')}</dt>
                  <dd className="text-[#2E7D32]">{formatPrice(order.total)}</dd>
                </div>
              </dl>
              {order.tracking_number && (
                <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                  {t('track.trackingNumber')}:{' '}
                  <span className="font-mono text-foreground">{order.tracking_number}</span>
                  {order.carrier && (
                    <span className="block mt-1">{order.carrier}</span>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-6">
            <h2 className="font-semibold mb-4">{t('track.items')}</h2>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div
                  key={item.product_id || index}
                  className="flex items-center gap-4 pb-4 border-b last:border-0 last:pb-0"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                    <ProductThumbnail
                      src={item.image}
                      alt={item.product_name ?? ''}
                      className="h-full w-full"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    {item.product_id ? (
                      <Link
                        to={`/product/${item.product_id}`}
                        className="font-medium hover:text-[#FF6000] transition-colors"
                      >
                        {item.product_name}
                      </Link>
                    ) : (
                      <p className="font-medium">{item.product_name}</p>
                    )}
                    {formatSelectedVariant(item.selected_variant) && (
                      <p className="text-sm text-blue-700">{formatSelectedVariant(item.selected_variant)}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t('track.quantity')}: {item.quantity}
                    </p>
                  </div>
                  <p className="font-medium">{formatPrice((item.price ?? 0) * (item.quantity ?? 1))}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filters = [
    { id: 'all', label: t('orders.filterAll') },
    { id: 'inProgress', label: t('orders.filterInProgress') },
    { id: 'cancelled', label: t('orders.filterCancelled') },
    { id: 'returns', label: t('orders.filterReturns') },
    { id: 'undeliverable', label: t('orders.filterUndeliverable') },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen pb-16 sm:pb-8" data-testid="orders-page">
      <div className="bg-white border-b sm:hidden">
        <div className="h-12 flex items-center justify-center relative px-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute left-3 p-1 text-gray-800"
            aria-label={t('common.back', 'Geri')}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-base font-bold">{t('orders.title')}</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-4 sm:py-8">
        <h1 className="hidden sm:block text-3xl font-bold mb-6">{t('orders.title')}</h1>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('orders.searchPlaceholder')}
            className="pl-9 h-11 rounded-lg bg-white border-gray-200"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1 scrollbar-none">
          {filters.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  active
                    ? 'border-[#FF6000] text-[#FF6000] bg-white font-medium'
                    : 'border-gray-300 text-gray-600 bg-white'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Package className="w-20 h-20 mx-auto text-muted-foreground/30 mb-6" />
            <h2 className="text-xl font-semibold mb-2">{t('orders.noOrders')}</h2>
            <p className="text-muted-foreground mb-8">{t('orders.noOrdersDescription')}</p>
            <Link to="/products">
              <Button className="rounded-full bg-[#FF6000] hover:bg-[#e55700] text-white">
                {t('orders.startShopping')}
              </Button>
            </Link>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border text-sm text-muted-foreground">
            {t('orders.noFilterResults')}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map((order) => {
              const thumbs = (order.items || []).slice(0, 3);
              const extra = Math.max(0, (order.items || []).length - 3);
              return (
                <Link
                  key={order.id}
                  to={`/orders/${order.id}`}
                  className="block bg-white border border-gray-200 rounded-xl p-3 sm:p-4 hover:border-gray-300 transition-colors"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex gap-3">
                    <div className="flex gap-1.5 shrink-0">
                      {thumbs.map((item, index) => (
                        <div key={index} className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden bg-gray-100">
                          <ProductThumbnail
                            src={item.image}
                            alt={item.product_name ?? ''}
                            className="h-full w-full"
                          />
                          {item.quantity > 1 && (
                            <span className="absolute top-0 left-0 text-[9px] font-bold text-white bg-[#FF6000] px-1 rounded-br">
                              x{item.quantity}
                            </span>
                          )}
                        </div>
                      ))}
                      {extra > 0 && (
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-md bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                          +{extra}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-700">{formatOrderDate(order.created_at)}</p>
                        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                      </div>
                      <div className="flex items-end justify-between gap-2 mt-auto">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          {getStatusIcon(order.status)}
                          <span>{getCardStatusLabel(order.status)}</span>
                        </div>
                        <p className="text-sm sm:text-base font-bold text-[#2E7D32]">
                          {formatPrice(order.total)}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {recommended.length > 0 && (
          <div className="mt-8">
            <h2 className="text-base font-bold mb-3">{t('orders.recentlyViewed')}</h2>
            <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:overflow-visible">
              {recommended.map((product) => {
                const compare = Number(product.compare_price || product.original_price) || 0;
                const price = Number(product.price) || 0;
                const discount = compare > price && price > 0
                  ? Math.round((1 - price / compare) * 100)
                  : 0;
                return (
                  <div
                    key={product.id}
                    className="bg-white border border-gray-200 rounded-xl p-2.5 min-w-[240px] sm:min-w-0 flex gap-2.5 relative"
                  >
                    <Link to={buildProductPath(i18n.language, product)} className="w-20 h-20 rounded-md overflow-hidden bg-gray-100 shrink-0">
                      <ProductThumbnail
                        src={product.images?.[0]}
                        alt={product.name}
                        className="h-full w-full"
                      />
                    </Link>
                    <div className="flex-1 min-w-0 pr-7">
                      <Link to={buildProductPath(i18n.language, product)} className="text-xs font-medium text-gray-900 line-clamp-2 hover:text-[#FF6000]">
                        {product.name}
                      </Link>
                      <div className="mt-1 flex items-center gap-1 flex-wrap">
                        {discount > 0 && (
                          <>
                            <span className="text-[11px] text-gray-400 line-through">
                              {formatPrice(compare, productPriceCurrency(product))}
                            </span>
                            <span className="text-[10px] font-semibold text-white bg-[#2E7D32] rounded px-1">
                              %{discount}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-sm font-bold text-[#2E7D32] mt-0.5">
                        {formatPrice(price, productPriceCurrency(product))}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="absolute bottom-2 right-2 w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-[#FF6000] hover:bg-orange-50"
                      onClick={async (e) => {
                        e.preventDefault();
                        if (productNeedsVariant(product)) {
                          navigate(buildProductPath(i18n.language, product));
                          return;
                        }
                        try {
                          await addItem(product, 1);
                          toast.success(t('cart.addedToCart'));
                        } catch {
                          toast.error(t('common.error'));
                        }
                      }}
                      aria-label={t('cart.addToCart', 'Sepete ekle')}
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
